import React, { useState } from "react";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import { TextEdit } from "./ui/TextEdit";
import fetchWithCredentials from "apiClient";
import { useManagers } from "context/ManagersContext";
import { useAuth } from "context/AuthContext";

interface InviteManagerDialogProps {
  open: boolean;
  onClose: () => void;
}

export function InviteManagerDialog({
  open,
  onClose,
}: InviteManagerDialogProps) {
  const { refresh } = useManagers();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await fetchWithCredentials("/manager-api/managers/invite/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, phone: phone.trim() || null }),
      });

      setSuccess(true);
      setName("");
      setEmail("");
      setPhone("");
      refresh();

      // Close dialog after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(
        err.message ||
          (locale === "es"
            ? "No se pudo enviar la invitación. Intente de nuevo."
            : "Failed to send invitation. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName("");
    setEmail("");
    setPhone("");
    setError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={locale === "es" ? "Invitar usuario" : "Invite user"}
    >
      <div className="max-w-md w-full">
        {success ? (
          <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {locale === "es"
              ? "¡Invitación enviada con éxito!"
              : "Invitation sent successfully!"}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <TextEdit
                id="name"
                name="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                label={locale === "es" ? "Nombre" : "Name"}
                placeholder={locale === "es" ? "Nombre completo" : "Full name"}
              />
            </div>
            <div className="mb-4">
              <TextEdit
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                label={locale === "es" ? "Correo electrónico" : "Email"}
                placeholder={
                  locale === "es" ? "usuario@ejemplo.com" : "user@example.com"
                }
              />
            </div>
            <div className="mb-4">
              <TextEdit
                id="phone"
                name="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                label={locale === "es" ? "Teléfono" : "Phone"}
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={handleClose}
                variant="secondary"
                size="sm"
              >
                {locale === "es" ? "Cancelar" : "Cancel"}
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={loading || !name || !email}
              >
                {loading
                  ? locale === "es"
                    ? "Enviando..."
                    : "Sending..."
                  : locale === "es"
                    ? "Enviar invitación"
                    : "Send invitation"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
